import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { getTimeSlotDisplay } from "@/utils/calendarGenerator";
import { Calendar, Clock, Mail, MapPin, Phone, User, FileText } from "lucide-react";

interface Appointment {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  service_id: string;
  services: { name: string } | null;
  preferred_date: string;
  preferred_time_slot: string;
  address: string;
  city: string;
  zip_code: string;
  appointment_type: string;
  status: string;
  notes: string | null;
  confirmed_datetime: string | null;
  created_at: string;
}

interface AppointmentDetailsDialogProps {
  appointment: Appointment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AppointmentDetailsDialog({
  appointment,
  open,
  onOpenChange,
}: AppointmentDetailsDialogProps) {
  if (!appointment) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Appointment Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Status Badge */}
          <div>
            <Badge
              variant={
                appointment.status === "pending"
                  ? "secondary"
                  : appointment.status === "confirmed"
                  ? "default"
                  : appointment.status === "completed"
                  ? "outline"
                  : "destructive"
              }
              className="text-sm"
            >
              {appointment.status.toUpperCase()}
            </Badge>
          </div>

          {/* Customer Information */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              Customer Information
            </h3>
            <div className="space-y-2 text-sm">
              <p>
                <strong>Name:</strong> {appointment.customer_name}
              </p>
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <a
                  href={`mailto:${appointment.customer_email}`}
                  className="text-primary hover:underline"
                >
                  {appointment.customer_email}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <a
                  href={`tel:${appointment.customer_phone}`}
                  className="text-primary hover:underline"
                >
                  {appointment.customer_phone}
                </a>
              </p>
            </div>
          </div>

          {/* Location */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Location
            </h3>
            <p className="text-sm">
              {appointment.address}
              <br />
              {appointment.city}, NY {appointment.zip_code}
            </p>
          </div>

          {/* Service Details */}
          <div>
            <h3 className="font-semibold mb-3">Service Details</h3>
            <div className="space-y-2 text-sm">
              <p>
                <strong>Service:</strong> {appointment.services?.name || "N/A"}
              </p>
              <p>
                <strong>Type:</strong>{" "}
                <span className="capitalize">{appointment.appointment_type}</span>
              </p>
            </div>
          </div>

          {/* Date & Time */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Preferred Date & Time
            </h3>
            <div className="space-y-2 text-sm">
              <p className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {format(new Date(appointment.preferred_date), "EEEE, MMMM d, yyyy")}
              </p>
              <p className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {getTimeSlotDisplay(appointment.preferred_time_slot)}
              </p>
            </div>
          </div>

          {/* Confirmed DateTime (if exists) */}
          {appointment.confirmed_datetime && (
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Confirmed Date & Time
              </h3>
              <p className="text-sm">
                {format(new Date(appointment.confirmed_datetime), "EEEE, MMMM d, yyyy 'at' h:mm a")}
              </p>
            </div>
          )}

          {/* Notes */}
          {appointment.notes && (
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Notes
              </h3>
              <p className="text-sm bg-muted p-3 rounded-md">{appointment.notes}</p>
            </div>
          )}

          {/* Metadata */}
          <div className="pt-4 border-t">
            <p className="text-xs text-muted-foreground">
              Booking ID: {appointment.id}
            </p>
            <p className="text-xs text-muted-foreground">
              Created: {format(new Date(appointment.created_at), "MMM d, yyyy 'at' h:mm a")}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
